// ops / route — the operator's Ops pages' URLs, pure.
//
//   /ops                 Overview (the Ops home)
//   /ops/feedback        Feedback (?report=<id> opens that report)
//   /ops/ai-usage…       AI usage and cost (its own route module)
//   /ops/accounts        Accounts
//
// The pages used to live under /admin/…; those links keep working: the
// mount rewrites them to their /ops/… address before it renders.

export type { OpsPage };
export {
    FEEDBACK_PATH,
    feedbackReportHref,
    OPS_ACCOUNTS_PATH,
    OPS_PATH,
    openReportOf,
    opsPageOf,
    opsPathOfLegacy,
};

import { AI_USAGE_PATH, isAiUsagePath } from "../aiUsage/route.js";

type OpsPage = "overview" | "feedback" | "ai-usage" | "accounts";

const OPS_PATH = "/ops";
const FEEDBACK_PATH = "/ops/feedback";
const OPS_ACCOUNTS_PATH = "/ops/accounts";

const trimmed = (pathname: string): string =>
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

// Which Ops page a path is, if any.
const opsPageOf = (pathname: string): OpsPage | undefined => {
    const p = trimmed(pathname);
    if (p === OPS_PATH) return "overview";
    if (p === FEEDBACK_PATH) return "feedback";
    if (p === OPS_ACCOUNTS_PATH) return "accounts";
    if (isAiUsagePath(p)) return "ai-usage";
    return undefined;
};

// The /ops/… address of a page's old /admin/… address (query kept), or
// undefined when the path is not one of them.
const opsPathOfLegacy = (
    pathname: string,
    search: string,
): string | undefined => {
    const p = trimmed(pathname);
    if (p === "/admin/feedback") return `${FEEDBACK_PATH}${search}`;
    if (p === "/admin/ai-usage" || p.startsWith("/admin/ai-usage/"))
        return `${AI_USAGE_PATH}${p.slice("/admin/ai-usage".length)}${search}`;
    return undefined;
};

// The report a Feedback link asks to open (from the Overview).
const openReportOf = (search: string): string | undefined => {
    const id = new URLSearchParams(search).get("report")?.trim();
    return id === undefined || id === "" ? undefined : id;
};

const feedbackReportHref = (reportId: string): string =>
    `${FEEDBACK_PATH}?${new URLSearchParams({ report: reportId }).toString()}`;
